import React, { useState, useEffect } from 'react'
import { supabase } from '../supabaseClient'
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Typography,
  FormControlLabel,
  Checkbox,
  Alert,
  Stack,
  Chip,
  Grid,
  Paper,
  Switch,
  useTheme,
  styled,
  Divider,
} from '@mui/material'

import {
  WbSunny as MorningIcon,
  Brightness3 as AfternoonIcon,
  Info as InfoIcon,
  Check as CheckIcon,
  EventAvailable as CalendarIcon,
  AccessTime as AccessTimeIcon,
} from '@mui/icons-material'

import { useTranslation } from 'react-i18next'

// Custom styled components
const DayCard = styled(Paper)(({ theme, isselected }) => ({
  padding: theme.spacing(2),
  borderRadius: theme.shape.borderRadius,
  boxShadow: isselected === 'true' ? theme.shadows[3] : theme.shadows[1],
  transition: 'all 0.2s ease-in-out',
  border:
    isselected === 'true' ? `2px solid ${theme.palette.primary.main}` : '2px solid transparent',
  backgroundColor:
    isselected === 'true'
      ? alpha(theme.palette.primary.main, 0.05)
      : theme.palette.background.paper,
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.shadows[4],
  },
  cursor: 'pointer',
}))

// Helper function to add alpha to colors
function alpha(color, value) {
  return (
    color +
    String(Math.round(value * 255))
      .toString(16)
      .padStart(2, '0')
  )
}

// Map day of week to database column names
const dayToColumnMap = {
  Monday: 'mon',
  Tuesday: 'tue',
  Wednesday: 'wed',
  Thursday: 'thur',
  Friday: 'fri',
  Saturday: 'sat',
  Sunday: 'sun',
}

// DaysOfWeek array
const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']

// Default schedule structure
const defaultSchedule = {
  Monday: { selected: false, morning: false, afternoon: false },
  Tuesday: { selected: false, morning: false, afternoon: false },
  Wednesday: { selected: false, morning: false, afternoon: false },
  Thursday: { selected: false, morning: false, afternoon: false },
  Friday: { selected: false, morning: false, afternoon: false },
  Saturday: { selected: false, morning: false, afternoon: false },
  Sunday: { selected: false, morning: false, afternoon: false },
}

export default function AvailabilitySelector() {
  const { t } = useTranslation()
  const theme = useTheme()

  const [loading, setLoading] = useState(false)
  const [saveSuccess, setSaveSuccess] = useState(false)
  const [error, setError] = useState('')
  const [availability, setAvailability] = useState(defaultSchedule)
  const [isFlexible, setIsFlexible] = useState(false)
  const [session, setSession] = useState(null)
  const [userTimezone, setUserTimezone] = useState(null)
  const [timezoneInfo, setTimezoneInfo] = useState({ code: null, label: null })

  // Get session on component mount
  useEffect(() => {
    async function getSession() {
      const { data, error } = await supabase.auth.getSession()
      if (error) {
        setError(error.message)
      } else {
        setSession(data.session)
      }
    }
    getSession()
  }, [])

  // Fetch the current availability and timezone once the session is available
  useEffect(() => {
    if (session) {
      fetchUserData()
    }
  }, [session])

  // Fetch availability and timezone from the database
  async function fetchUserData() {
    setLoading(true)
    try {
      // First fetch the user's timezone ID from profiles
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('*, platform_timezones(code, label) as timezone')
        .single()

      const timezoneData = profileData?.timezone

      if (profileError && profileError.code !== 'PGRST116') {
        setError(profileError.message)
      } else if (profileData && profileData.timezone_id) {
        setUserTimezone(profileData.timezone_id)
        if (timezoneData) {
          setTimezoneInfo({
            code: timezoneData.code,
            label: timezoneData.label,
          })
        }
      }

      // Then fetch availability data
      const { data, error } = await supabase.from('profile_availability').select('*').single()

      if (error && error.code !== 'PGRST116') {
        // PGRST116 is returned if no row exists – that's fine
        setError(error.message)
      } else if (data) {
        // Convert the database format to the UI format
        const newFormat = { ...defaultSchedule }

        // Process each day from the database
        daysOfWeek.forEach((day) => {
          const dbColumn = dayToColumnMap[day]
          const availabilityChoice = data[dbColumn]

          if (availabilityChoice) {
            newFormat[day].selected = true

            if (availabilityChoice === 'morning') {
              newFormat[day].morning = true
              newFormat[day].afternoon = false
            } else if (availabilityChoice === 'afternoon') {
              newFormat[day].morning = false
              newFormat[day].afternoon = true
            } else if (availabilityChoice === 'anytime') {
              newFormat[day].morning = true
              newFormat[day].afternoon = true
            }
          }
        })

        setAvailability(newFormat)
        setIsFlexible(data.is_flexible || false)
      }
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  // Handle day selection
  const handleDaySelect = (day) => {
    setAvailability((prev) => ({
      ...prev,
      [day]: {
        ...prev[day],
        selected: !prev[day].selected,
        // If we're selecting a previously unselected day, default to both morning and afternoon
        morning: !prev[day].selected ? true : prev[day].morning,
        afternoon: !prev[day].selected ? true : prev[day].afternoon,
      },
    }))
  }

  // Handle time preference change
  const handleTimeChange = (day, timeOfDay) => {
    setAvailability((prev) => {
      // Toggle the selected time (morning or afternoon)
      const newValue = !prev[day][timeOfDay]

      // If both morning and afternoon are being turned off, also unselect the day
      const otherTimeOfDay = timeOfDay === 'morning' ? 'afternoon' : 'morning'
      const shouldKeepDaySelected = newValue || prev[day][otherTimeOfDay]

      return {
        ...prev,
        [day]: {
          ...prev[day],
          [timeOfDay]: newValue,
          selected: shouldKeepDaySelected,
        },
      }
    })
  }

  // Convert UI format to database format for saving
  const convertToDatabaseFormat = () => {
    const dbData = {
      profile_id: session.user.id,
      is_flexible: isFlexible,
      updated_at: new Date(),
    }

    // For each day, determine the availability_choice value
    daysOfWeek.forEach((day) => {
      const { selected, morning, afternoon } = availability[day]
      const dbColumn = dayToColumnMap[day]

      if (!selected) {
        dbData[dbColumn] = null
      } else if (morning && afternoon) {
        dbData[dbColumn] = 'anytime'
      } else if (morning) {
        dbData[dbColumn] = 'morning'
      } else if (afternoon) {
        dbData[dbColumn] = 'afternoon'
      } else {
        // This shouldn't happen with the current UI logic,
        // but handle it just in case
        dbData[dbColumn] = null
      }
    })

    return dbData
  }

  // Save availability to database
  const handleSave = async () => {
    if (!session) return

    setLoading(true)
    setError('')
    setSaveSuccess(false)

    try {
      const dbData = convertToDatabaseFormat()
      const { error } = await supabase.from('profile_availability').upsert(dbData)

      if (error) throw error
      setSaveSuccess(true)

      // Clear success message after 3 seconds
      setTimeout(() => {
        setSaveSuccess(false)
      }, 3000)
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const anyDaySelected = Object.values(availability).some((day) => day.selected)

  // Format the timezone for display
  const formatTimeZone = (timezoneCode) => {
    if (!timezoneCode) return ''

    try {
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: timezoneCode,
        timeZoneName: 'long',
      })

      const timeZoneName =
        formatter.formatToParts(new Date()).find((part) => part.type === 'timeZoneName')?.value ||
        timezoneCode

      return timeZoneName
    } catch (error) {
      console.error('Error formatting timezone:', error)
      return timezoneCode
    }
  }

  // If no session, show loading
  if (!session) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="300px">
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto' }}>
      {/* Timezone information banner */}
      <Alert severity="info" icon={<AccessTimeIcon />} sx={{ mb: 4, borderRadius: 2 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 500, mb: 0.5 }}>
          {t('settings.availability.timezoneNote', 'Time Zone Information')}
        </Typography>
        <Typography variant="body2">
          {t(
            'settings.availability.timezoneExplanation',
            'Your availability is set according to your time zone:'
          )}{' '}
          <strong>
            {timezoneInfo.label ||
              formatTimeZone(timezoneInfo.code) ||
              t('settings.timezone.notSet', 'Not set')}
          </strong>
        </Typography>
        <Button variant="text" component="a" href="/user?tab=profile" size="small" sx={{ mt: 1 }}>
          {t('settings.availability.changeTimezone', 'Change time zone')}
        </Button>
      </Alert>

      {/* Introduction Section */}
      <Stack spacing={3} sx={{ mb: 4 }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          {t('settings.availability.title', 'When are you available for viewings?')}
        </Typography>

        <Typography variant="body1" color="text.secondary">
          {t(
            'settings.availability.description',
            "Let us know when you're typically available to view properties. This helps us schedule viewings that work with your schedule."
          )}
        </Typography>

        {/* Info Banner */}
        <Alert
          severity="info"
          icon={<InfoIcon />}
          sx={{
            borderRadius: 2,
            '& .MuiAlert-message': { width: '100%' },
          }}
        >
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
            {t('settings.availability.tips.title', 'Quick Tip')}
          </Typography>
          <Typography variant="body2">
            {t(
              'settings.availability.tips.3',
              "Select the days you're available and specify morning or afternoon preferences. The more flexible you are, the easier it will be to schedule viewings."
            )}
          </Typography>
        </Alert>
      </Stack>

      {/* Success/Error messages */}
      {saveSuccess && (
        <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>
          {t('settings.availability.saveSuccess', 'Your availability has been saved successfully!')}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {/* "I'm flexible" toggle */}
      <Card
        sx={{
          mb: 4,
          boxShadow: isFlexible ? `0 0 0 2px ${theme.palette.primary.main}` : undefined,
          borderRadius: 2,
          transition: 'all 0.2s',
        }}
      >
        <CardContent>
          <FormControlLabel
            control={
              <Switch
                checked={isFlexible}
                onChange={(e) => setIsFlexible(e.target.checked)}
                color="primary"
              />
            }
            label={
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                  {t('settings.availability.flexibleNote', "I'm flexible with my schedule")}
                </Typography>
                {isFlexible && (
                  <Chip
                    size="small"
                    color="primary"
                    label={t('settings.availability.recommended', 'Recommended')}
                    icon={<CheckIcon />}
                  />
                )}
              </Stack>
            }
          />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {t(
              'settings.availability.flexibleExplanation',
              'Select this if you have a flexible schedule and can accommodate viewings outside your specified times. This increases your chances of getting more viewing options.'
            )}
          </Typography>
        </CardContent>
      </Card>

      {/* Days Section */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <CalendarIcon color="primary" />
        <Typography variant="h6">
          {t('settings.availability.selectDays', 'Select your available days')}
        </Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t(
          'settings.availability.selectDaysHelper',
          'Click on a day card to select it, then choose morning and/or afternoon availability.'
        )}
      </Typography>

      <Grid container spacing={2} sx={{ mb: 4 }}>
        {daysOfWeek.map((day) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={day}>
            <DayCard
              isselected={availability[day].selected ? 'true' : 'false'}
              onClick={() => handleDaySelect(day)}
            >
              <Stack spacing={2}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    cursor: 'pointer',
                  }}
                  onClick={(e) => {
                    // Prevent click from propagating to parent (which would also trigger day select)
                    e.stopPropagation()
                    handleDaySelect(day)
                  }}
                >
                  <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                    {t(`days.${day.toLowerCase()}`, day)}
                  </Typography>
                  <Checkbox
                    checked={availability[day].selected}
                    onChange={(e) => {
                      e.stopPropagation()
                      handleDaySelect(day)
                    }}
                    color="primary"
                    onClick={(e) => e.stopPropagation()}
                  />
                </Box>

                {availability[day].selected && (
                  <>
                    <Divider />
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {t('settings.availability.timePreference', 'Time preference')}
                    </Typography>

                    <Stack spacing={1}>
                      <Button
                        variant={availability[day].morning ? 'contained' : 'outlined'}
                        onClick={(e) => {
                          e.stopPropagation()
                          handleTimeChange(day, 'morning')
                        }}
                        startIcon={<MorningIcon />}
                        fullWidth
                        sx={{
                          justifyContent: 'flex-start',
                          py: 1.5,
                          transition: 'all 0.2s ease',
                        }}
                      >
                        {t('settings.availability.morning', 'Morning')}
                      </Button>

                      <Button
                        variant={availability[day].afternoon ? 'contained' : 'outlined'}
                        onClick={(e) => {
                          e.stopPropagation()
                          handleTimeChange(day, 'afternoon')
                        }}
                        startIcon={<AfternoonIcon />}
                        fullWidth
                        sx={{
                          justifyContent: 'flex-start',
                          py: 1.5,
                          transition: 'all 0.2s ease',
                        }}
                      >
                        {t('settings.availability.afternoon', 'Afternoon')}
                      </Button>
                    </Stack>
                  </>
                )}
              </Stack>
            </DayCard>
          </Grid>
        ))}
      </Grid>

      {/* Summary and Save Button */}
      <Card sx={{ mb: 4, borderRadius: 2 }}>
        <CardContent>
          <Typography variant="subtitle1" sx={{ fontWeight: 500, mb: 2 }}>
            {t('settings.availability.summary', 'Availability summary')}
          </Typography>

          {!anyDaySelected && !isFlexible ? (
            <Typography variant="body2" color="warning.main">
              {t(
                'settings.availability.noSelection',
                "You haven't selected any days. Please select at least one day or set yourself as flexible."
              )}
            </Typography>
          ) : (
            <Box>
              {isFlexible && (
                <Chip
                  label={t('settings.availability.flexibleChip', 'Flexible schedule')}
                  color="primary"
                  variant="outlined"
                  sx={{ mr: 1, mb: 1 }}
                />
              )}

              {Object.entries(availability).map(
                ([day, { selected, morning, afternoon }]) =>
                  selected && (
                    <Chip
                      key={day}
                      label={`${t(`days.${day.toLowerCase()}`, day)}: ${
                        morning && afternoon
                          ? t('settings.availability.allDay', 'All Day')
                          : morning
                          ? t('settings.availability.morning', 'Morning')
                          : t('settings.availability.afternoon', 'Afternoon')
                      }`}
                      variant="outlined"
                      sx={{ mr: 1, mb: 1 }}
                    />
                  )
              )}
            </Box>
          )}
        </CardContent>
      </Card>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          color="primary"
          size="large"
          onClick={handleSave}
          disabled={loading || (!anyDaySelected && !isFlexible)}
          startIcon={loading ? <CircularProgress size={20} /> : null}
          sx={{ minWidth: 160 }}
        >
          {loading ? t('common.saving', 'Saving...') : t('common.save', 'Save')}
        </Button>
      </Box>
    </Box>
  )
}
